<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";

/*
  SubscriptionsMarketingView.vue

  A marketing-friendly subscriptions page that:
  - Shows a top site header (like the landing page)
  - Forces light theme while mounted
  - Displays three easily editable plan cards (Monthly, Yearly, 100 Years)
  - Sends users to the learn.* domain for login/checkout flows

  To edit plans, update the `plans` array below (name, priceDisplay, features).
*/

let previousTheme = null;

// Editable plan definitions
const plans = ref([
  {
    key: "monthly",
    name: "Monthly",
    priceDisplay: "$2.99 / month",
    badge: "Most flexible",
    description: "Great for getting started.",
    features: [
      "Access to all features and languages",
      "Unlimited cards",
      "Email Support",
      "Cancel anytime",
    ],
  },
  {
    key: "yearly",
    name: "Yearly",
    priceDisplay: "$27.99 / year",
    badge: "Save 20%",
    description: "Best value billed annually.",
    features: [
      "Everything in Monthly",
      "10% discount over monthly",
      "Phone support",
      "Early access to beta features",
    ],
    popular: true,
  },
  {
    key: "100yearly",
    name: "100 Years",
    priceDisplay: "$49.99 / 100 years",
    badge: "Simple flat rate",
    description: "A straightforward lifetime option, unless you're immortal.",
    features: [
      "Everything in Yearly and Monthly",
      "Flat rate for lifetime usage",
      "Perfect for power learners",
      "Supports the development of new features",
    ],
  },
]);

// Selected plan (for visual emphasis and "Get started" intent)
const selectedPlanKey = ref(
  plans.value.find((p) => p.popular)?.key || "yearly"
);
// Optional: preselect a plan when arriving with ?plan= in the URL
try {
  const params = new URLSearchParams(window.location.search);
  const planParam = params.get("plan");
  if (planParam && plans.value.some((p) => p.key === planParam)) {
    selectedPlanKey.value = planParam;
  }
} catch (e) {
  console.warn("Plan preselect parsing failed:", e);
}
const selectPlan = (key) => {
  selectedPlanKey.value = key;
};

function isLocalHost(h) {
  return h === "localhost" || h === "127.0.0.1" || h === "::1";
}
function getApexDomain(h) {
  const parts = (h || "").split(".");
  if (parts.length < 2) return h || "localhost";
  return parts.slice(-2).join(".");
}
function getLearnBase() {
  const { protocol, hostname, port } = window.location;
  if (isLocalHost(hostname)) {
    const p = port ? `:${port}` : "";
    // In dev, assume learn.<host> with same port (supports hosts entries)
    return `${protocol}//learn.${hostname}${p}`;
  }
  const apex = getApexDomain(hostname);
  return `${protocol}//learn.${apex}`;
}
const learnBase = getLearnBase();

function subscribe(plan) {
  // Always drive checkout via the learn.* app
  const url = `${learnBase}/subscriptions?plan=${encodeURIComponent(plan.key)}`;
  window.location.href = url;
}

onMounted(() => {
  // Force light theme here (marketing pages)
  const root = document.documentElement;
  previousTheme = root.getAttribute("data-theme");
  root.setAttribute("data-theme", "light");
});
onBeforeUnmount(() => {
  const root = document.documentElement;
  if (previousTheme) {
    root.setAttribute("data-theme", previousTheme);
  } else {
    root.removeAttribute("data-theme");
  }
});
</script>

<template>
  <div class="subs-marketing">
    <!-- Site Header (Marketing) -->
    <header class="site-header">
      <div class="container header-inner">
        <a class="brand" href="/">
          <img class="brand-logo" src="@/assets/Logo.svg" alt="Lexical logo" />
          <span class="brand-name">Lexical</span>
        </a>
        <nav class="nav-cta">
          <a class="btn btn-ghost" :href="`${learnBase}/login`">Log in</a>
          <a
            class="btn btn-accent"
            :href="`${learnBase}/login?plan=${encodeURIComponent(
              selectedPlanKey
            )}&redirect=%2Fsubscriptions`"
          >
            Get started
          </a>
        </nav>
      </div>
    </header>

    <!-- Header copy -->
    <section class="hero">
      <div class="container hero-inner">
        <div class="hero-copy">
          <h1>Choose your plan</h1>
          <p class="subcopy">
            Pick the plan that fits your learning pace. You can change or cancel
            anytime.
          </p>
        </div>
      </div>
    </section>

    <!-- Plan cards -->
    <section class="plans">
      <div class="container">
        <div class="cards">
          <article
            v-for="plan in plans"
            :key="plan.key"
            class="card"
            :class="{
              popular: !!plan.popular,
              selected: selectedPlanKey === plan.key,
            }"
            @click="selectPlan(plan.key)"
          >
            <div class="card-header">
              <h2 class="name">{{ plan.name }}</h2>
              <span v-if="plan.badge" class="badge">{{ plan.badge }}</span>
            </div>

            <div class="price">{{ plan.priceDisplay }}</div>
            <p class="desc">{{ plan.description }}</p>

            <ul class="features">
              <li v-for="f in plan.features" :key="f">{{ f }}</li>
            </ul>

            <button class="btn btn-subscribe" @click.stop="subscribe(plan)">
              Subscribe
            </button>
          </article>
        </div>

        <div class="footnote">
          By subscribing you agree to our
          <a href="/terms">Terms and Privacy Policy</a>. Subscriptions renew
          automatically unless cancelled.
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* Layout primitives */
.container {
  width: min(1100px, calc(100% - 32px));
  margin-inline: auto;
}
.subs-marketing {
  color: var(--text-color);
  background: var(--app-bg);
}

/* Header */
.site-header {
  position: sticky;
  top: 0;
  backdrop-filter: saturate(180%) blur(8px);
  background: color-mix(in oklab, var(--app-bg) 86%, transparent);
  border-bottom: 1px solid
    color-mix(in oklab, var(--text-color) 8%, transparent);
  z-index: 10;
}
.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}
.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
}
.brand-logo {
  width: 28px;
  height: 28px;
}
.brand-name {
  font-weight: 700;
  letter-spacing: 0.2px;
  color: var(--text-color);
}
.nav-cta {
  display: flex;
  gap: 8px;
}

/* Buttons */
.btn {
  --btn-bg: var(--hover-bg);
  --btn-fg: var(--text-color);
  --btn-br: 10px;
  --btn-pad: 10px 14px;

  background: var(--btn-bg);
  color: var(--btn-fg);
  border: none;
  border-radius: var(--btn-br);
  padding: var(--btn-pad);
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.05s ease, background 0.2s ease, opacity 0.2s ease;
}
.btn:hover {
  transform: translateY(-1px);
}
.btn:active {
  transform: translateY(0);
}
.btn-ghost {
  background: transparent;
  border: 1px solid color-mix(in oklab, var(--text-color) 12%, transparent);
}
.btn-accent {
  --btn-bg: var(--accent);
  --btn-fg: #fff;
}

/* Hero */
.hero {
  padding: clamp(28px, 5vw, 48px) 0 10px;
  background: radial-gradient(
      1200px 400px at 80% -200px,
      color-mix(in oklab, var(--accent) 16%, transparent),
      transparent
    ),
    var(--app-bg);
}
.hero-inner {
  display: grid;
}
.hero-copy h1 {
  margin: 0 0 6px;
  font-size: clamp(1.8rem, 1.2rem + 2.5vw, 2.6rem);
  letter-spacing: -0.02em;
}
.hero-copy .subcopy {
  margin: 0;
  color: color-mix(in oklab, var(--text-color) 80%, transparent);
}

/* Plans */
.plans {
  padding: 10px 0 clamp(28px, 5vw, 56px);
}
.cards {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 14px;
}
.card {
  background: var(--sidebar-bg);
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid transparent;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.06s ease,
    box-shadow 0.15s ease;
}
.card:hover {
  transform: translateY(-1px);
  border-color: color-mix(in oklab, var(--text-color) 10%, transparent);
}
.card.selected {
  border-color: var(--accent);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.card.popular {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.name {
  margin: 0;
  font-size: 1.1rem;
}
.badge {
  background: var(--accent);
  color: #fff;
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 0.8rem;
  font-weight: 600;
}
.price {
  font-size: 1.4rem;
  font-weight: 800;
  margin: 6px 0 8px;
}
.desc {
  margin: 0 0 8px;
  color: color-mix(in oklab, var(--text-color) 78%, transparent);
}
.features {
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
  display: grid;
  gap: 6px;
}
.features li {
  display: flex;
  gap: 8px;
}
.features li::before {
  content: "✓";
  color: var(--accent);
  font-weight: 800;
}

/* Subscribe button */
.btn-subscribe {
  width: 100%;
  padding: 10px 14px;
  background: #10b981; /* emerald-500 */
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.06s ease, background 0.15s ease;
}
.btn-subscribe:hover {
  transform: translateY(-1px);
  background: #0ea672;
}

/* Fine print */
.footnote {
  margin-top: 10px;
  font-size: 0.9rem;
  color: color-mix(in oklab, var(--text-color) 72%, transparent);
}

/* Responsive */
@media (max-width: 980px) {
  .cards {
    grid-template-columns: 1fr;
  }
}
</style>
